import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import BrimiLogo from "@/components/BrimiLogo";
import { Loader2, GraduationCap, Map, ClipboardList, Trophy } from "lucide-react";

const RIASEC_COLORS = { R:"#3B82F6", I:"#8B5CF6", A:"#FB7185", S:"#34D399", E:"#FBBF24", C:"#F97316" };
const CODE_NAMES = { R:"Realistic", I:"Investigative", A:"Artistic", S:"Social", E:"Enterprising", C:"Conventional" };

export default function ParentView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/parent/view/${token}`).then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Invalid link")).finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0B1120] text-white"><Loader2 className="w-6 h-6 animate-spin text-yellow-400" /></div>;
  if (err) return <div className="min-h-screen flex items-center justify-center bg-[#0B1120] text-white p-8 text-center"><div><p className="font-display text-3xl">Link invalid or expired</p><p className="text-slate-400 mt-2">Please ask your child to generate a new share link.</p></div></div>;

  const p = data.profile || {};
  const q = data.quiz || {};
  const r = data.roadmap || {};

  return (
    <div className="min-h-screen bg-[#0B1120] grid-noise text-white pb-20">
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <BrimiLogo size={36} withBadge showText textColor="white" subtitle="Parent View · Read-only" />
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 pt-10" data-testid="parent-view-page">
        <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-300">Progress snapshot for</p>
        <h1 className="font-display text-4xl md:text-5xl mt-2">{data.student?.name || "Student"}</h1>

        <div className="mt-6 brimi-card p-6">
          <div className="flex items-center justify-between">
            <p className="font-display text-2xl text-slate-900">Career Readiness</p>
            <p className="font-display text-4xl text-slate-900">{data.readiness_score}%</p>
          </div>
          <div className="h-2 bg-slate-100 rounded-full mt-3 overflow-hidden">
            <div className="h-full" style={{ width: `${data.readiness_score}%`, background: "linear-gradient(90deg, #FB7185, #FBBF24, #34D399)" }} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            <Stat icon={ClipboardList} label="Profile" done={!!data.profile} />
            <Stat icon={Trophy} label="Interest Quiz" done={!!data.quiz} />
            <Stat icon={Map} label="Roadmap" done={!!data.roadmap} />
            <Stat icon={GraduationCap} label="Colleges saved" done={data.saved_colleges?.length > 0} count={data.saved_colleges?.length} />
          </div>
        </div>

        {p.grade && (
          <div className="mt-6 brimi-card p-6">
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">Profile</p>
            <div className="grid md:grid-cols-2 gap-3 mt-3 text-sm text-slate-800">
              <Info label="Grade">Class {p.grade}</Info>
              <Info label="Stream">{p.stream || "—"}</Info>
              <Info label="Interests">{(p.interests || []).join(", ") || "—"}</Info>
              <Info label="Strengths">{(p.strengths || []).join(", ") || "—"}</Info>
              <Info label="Region preference">{p.region_pref}</Info>
              {p.goals && <Info label="Goals">{p.goals}</Info>}
            </div>
          </div>
        )}

        {q.scores && (
          <div className="mt-6 brimi-card p-6">
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">Interest profile (RIASEC)</p>
            <div className="mt-3 space-y-2">
              {Object.entries(q.scores).sort((a,b) => b[1]-a[1]).map(([code, val]) => (
                <div key={code}>
                  <div className="flex justify-between text-sm mb-1"><b className="text-slate-800">{code} — {CODE_NAMES[code]}</b><span className="text-slate-500">{val}%</span></div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full" style={{ width: `${val}%`, background: RIASEC_COLORS[code] }} /></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {r.career_target && (
          <div className="mt-6 brimi-card p-6">
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">Career target</p>
            <h2 className="font-display text-2xl text-slate-900 mt-1">{r.career_target}</h2>
            <p className="text-slate-700 mt-2">{r.summary}</p>
            {(r.milestones || []).length > 0 && (
              <div className="mt-4 space-y-3">
                {r.milestones.map((m, i) => (
                  <div key={i} className="p-3 rounded-xl border border-slate-100">
                    <p className="font-bold text-slate-800 text-sm">{i+1}. {m.title} <span className="text-xs text-slate-500">· {m.grade}</span></p>
                    {m.tasks?.length > 0 && <ul className="mt-1 text-xs text-slate-600 list-disc list-inside space-y-0.5">{m.tasks.slice(0,3).map((t,j) => <li key={j}>{t}</li>)}</ul>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {(data.saved_careers?.length > 0 || data.saved_colleges?.length > 0) && (
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <div className="brimi-card p-6">
              <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">Saved careers</p>
              <ul className="mt-3 space-y-1 text-slate-800 text-sm list-disc list-inside">
                {(data.saved_careers || []).map((c, i) => <li key={i}>{c.name}</li>)}
              </ul>
            </div>
            <div className="brimi-card p-6">
              <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">Saved colleges</p>
              <ul className="mt-3 space-y-1 text-slate-800 text-sm list-disc list-inside">
                {(data.saved_colleges || []).map((c, i) => <li key={i}>{c.name} <span className="text-slate-500">· {c.region}</span></li>)}
              </ul>
            </div>
          </div>
        )}

        <p className="text-xs text-slate-500 text-center mt-10">This is a read-only shareable view · Powered by BrimiAI CareerCoach</p>
      </div>
    </div>
  );
}

function Info({ label, children }) {
  return (
    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
      <p className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold">{label}</p>
      <p className="mt-0.5">{children}</p>
    </div>
  );
}

function Stat({ icon: Icon, label, done, count }) {
  return (
    <div className="p-3 rounded-xl border border-slate-100 text-center">
      <Icon className="w-5 h-5 mx-auto text-slate-500" />
      <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-bold mt-2">{label}</p>
      <p className="font-display text-xl text-slate-900">{count !== undefined ? count : (done ? "✓" : "—")}</p>
    </div>
  );
}
